import { PROFILE_CHAPTER_TITLES } from '../../constants/profileChapterTitles';
import { getCareDocumentOrganizedKnowledgeCards, getCareDocumentPurposeLabel, getCareDocumentResolvedSummary, getCareDocumentSearchText, getCareDocumentTitle, getCareDocumentType } from './careDocumentPresentation';

const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const getSourceKind = (source = {}) => normalizeText(
  source.sourceKind
  || source.sourceMetadata?.sourceKind
  || source.metadata?.source?.sourceKind
  || source.sourceType
  || source.metadata?.source?.sourceType
  || ''
).toLowerCase();

const getSourceDocumentType = (source = {}) => normalizeText(
  source.documentType
  || source.sourceMetadata?.documentType
  || source.metadata?.source?.documentType
  || ''
).toLowerCase();

const getSourceContext = (source = {}) => normalizeText(
  source.sourceContext
  || source.sourceMetadata?.sourceContext
  || source.metadata?.source?.sourceContext
  || ''
).toLowerCase();

const hasImageAttachments = (source = {}) => Boolean(
  (Array.isArray(source.imageAttachments) && source.imageAttachments.some((attachment) => Boolean(attachment?.url)))
  || (Array.isArray(source.sourceMetadata?.imageAttachments) && source.sourceMetadata.imageAttachments.some((attachment) => Boolean(attachment?.url)))
  || (Array.isArray(source.metadata?.source?.imageAttachments) && source.metadata.source.imageAttachments.some((attachment) => Boolean(attachment?.url)))
);

export const isLibraryImageSource = (source = {}) => {
  const sourceKind = getSourceKind(source);
  const documentType = getSourceDocumentType(source);

  return sourceKind === 'image'
    || source.sourceType === 'screenshot'
    || source.sourceMetadata?.sourceType === 'screenshot'
    || source.metadata?.source?.sourceType === 'screenshot'
    || documentType === 'image'
    || hasImageAttachments(source);
};

export const isLibraryDocumentSource = (source = {}) => {
  const sourceKind = getSourceKind(source);
  const sourceContext = getSourceContext(source);
  return (
    (sourceKind === 'file' || sourceKind === 'image' || sourceContext === 'document')
    && !isLibraryImageSource(source)
  );
};

const SUBJECT_PATTERNS = [
  {
    key: 'communication',
    label: PROFILE_CHAPTER_TITLES.COMMUNICATION,
    patterns: [
      /\bcommunication\b/i,
      /\baac\b/i,
      /\bspeech\b/i,
      /\blanguage\b/i,
      /\bexpress(?:ion|ive)?\b/i,
      /\bsign language\b/i,
    ],
  },
  {
    key: 'behavior',
    label: 'Behavior',
    patterns: [
      /\bbehavior\b/i,
      /\btrigger(?:s|ed)?\b/i,
      /\bwarning sign(?:s)?\b/i,
      /\bde-escalat/i,
      /\bmeltdown\b/i,
      /\belop(?:e|ement|ing)?\b/i,
      /\baggressive\b/i,
    ],
  },
  {
    key: 'school',
    label: 'School',
    patterns: [
      /\bschool\b/i,
      /\biep\b/i,
      /\bard\b/i,
      /\bclassroom\b/i,
      /\bteacher\b/i,
      /\bacademic\b/i,
      /\bstudent\b/i,
    ],
  },
  {
    key: 'health',
    label: 'Health',
    patterns: [
      /\bhealth\b/i,
      /\bmedical\b/i,
      /\bmedication\b/i,
      /\blab(?:s| result| report| work)?\b/i,
      /\bdischarge\b/i,
      /\bhospital\b/i,
      /\bdoctor\b/i,
      /\bsymptom\b/i,
      /\bpain\b/i,
      /\btherapy\b/i,
    ],
  },
  {
    key: 'safety',
    label: PROFILE_CHAPTER_TITLES.SAFETY,
    patterns: [
      /\bsafety\b/i,
      /\brisk\b/i,
      /\bemergency\b/i,
      /\bcrisis\b/i,
      /\bchoking\b/i,
      /\bself-harm\b/i,
      /\bfall(?:s|ing)?\b/i,
    ],
  },
  {
    key: 'what_helps',
    label: PROFILE_CHAPTER_TITLES.WHAT_HELPS,
    patterns: [
      /\bwhat helps\b/i,
      /\bhelpful\b/i,
      /\bstrategy\b/i,
      /\bsupport\b/i,
      /\baccommodation(?:s)?\b/i,
      /\bvisual schedule\b/i,
      /\bbreak card\b/i,
    ],
  },
  {
    key: 'daily_life',
    label: PROFILE_CHAPTER_TITLES.DAILY_LIFE,
    patterns: [
      /\bdaily life\b/i,
      /\broutine(?:s)?\b/i,
      /\bsleep\b/i,
      /\bfeeding\b/i,
      /\bhygiene\b/i,
      /\btoileting\b/i,
      /\bbathroom\b/i,
      /\bmealtime\b/i,
    ],
  },
];

const subjectPriority = SUBJECT_PATTERNS.reduce((acc, subject, index) => {
  acc[subject.label] = index;
  return acc;
}, {});

const getCardSearchText = (source = {}) => getCareDocumentSearchText(source);

const getSourceDetailCount = (source = {}) => {
  const summary = source.documentFindingSummary || source.metadata?.careSourceProcessing?.documentFindingSummary || {};
  const directCount = Number(summary?.itemCount || 0);
  if (directCount > 0) return directCount;

  const suggestionCount = Number(source.careKnowledgeSuggestionSummary?.itemCount || 0);
  if (suggestionCount > 0) return suggestionCount;

  return getCareDocumentOrganizedKnowledgeCards(source)
    .reduce((total, card) => total + (Array.isArray(card.items) ? card.items.length : 0), 0);
};

const getOrganizedKnowledgeText = (source = {}) => getCareDocumentOrganizedKnowledgeCards(source)
  .flatMap((card) => [card.bucketLabel, card.title, ...(card.items || []).map((item) => item.text)])
  .filter(Boolean)
  .join(' ')
  .toLowerCase();

const getSubjectLabelsFromText = (text = '') => SUBJECT_PATTERNS
  .filter((subject) => subject.patterns.some((pattern) => pattern.test(text)))
  .map((subject) => subject.label);

const getSubjectLabelsFromSource = (source = {}) => {
  const titleText = [
    getCareDocumentTitle(source),
    getCareDocumentType(source),
    getCareDocumentPurposeLabel(source),
    getCareDocumentResolvedSummary(source),
    getCardSearchText(source),
    getOrganizedKnowledgeText(source),
  ].filter(Boolean).join(' ').toLowerCase();

  const labels = new Set(getSubjectLabelsFromText(titleText));

  const profileChapterId = normalizeText(
    source.profileChapterId
    || source.sourceMetadata?.profileChapterId
    || source.metadata?.source?.profileChapterId
    || ''
  ).toLowerCase();

  if (profileChapterId === 'communication') labels.add(PROFILE_CHAPTER_TITLES.COMMUNICATION);
  if (profileChapterId === 'what_helps') labels.add(PROFILE_CHAPTER_TITLES.WHAT_HELPS);
  if (profileChapterId === 'daily_life') labels.add(PROFILE_CHAPTER_TITLES.DAILY_LIFE);
  if (profileChapterId === 'safety') labels.add(PROFILE_CHAPTER_TITLES.SAFETY);
  if (profileChapterId === 'health_care') labels.add('Health');
  if (profileChapterId === 'school') labels.add('School');

  if (!labels.size) {
    if (/\b(iep|ard|school)\b/i.test(titleText)) labels.add('School');
    if (/\b(communication|aac|speech|language)\b/i.test(titleText)) labels.add(PROFILE_CHAPTER_TITLES.COMMUNICATION);
    if (/\b(behavior|trigger|warning sign|de-escalat|meltdown|elop|aggressive)\b/i.test(titleText)) labels.add('Behavior');
    if (/\b(health|medical|medication|discharge|hospital|doctor|symptom|pain|therapy|lab)\b/i.test(titleText)) labels.add('Health');
    if (/\b(safety|risk|emergency|crisis|choking|fall)\b/i.test(titleText)) labels.add(PROFILE_CHAPTER_TITLES.SAFETY);
    if (/\b(what helps|helpful|strategy|support|accommodation|visual schedule|break card)\b/i.test(titleText)) labels.add(PROFILE_CHAPTER_TITLES.WHAT_HELPS);
    if (/\b(daily life|routine|sleep|feeding|hygiene|toileting|bathroom|mealtime)\b/i.test(titleText)) labels.add(PROFILE_CHAPTER_TITLES.DAILY_LIFE);
  }

  return [...labels].sort((left, right) => (subjectPriority[left] ?? 99) - (subjectPriority[right] ?? 99));
};

export const getLibrarySourceSummary = (source = {}) => {
  const summary = source.documentFindingSummary || source.metadata?.careSourceProcessing?.documentFindingSummary || {};
  const goalCount = Number(summary?.countsByType?.goalInfo || 0);
  if (goalCount > 0) {
    return `${goalCount} goal${goalCount === 1 ? '' : 's'} found`;
  }

  const detailCount = getSourceDetailCount(source);
  if (detailCount > 0) {
    return `${detailCount} detail${detailCount === 1 ? '' : 's'} found`;
  }

  const resolvedSummary = getCareDocumentResolvedSummary(source);
  if (resolvedSummary) {
    return resolvedSummary;
  }

  const organizedCount = getSourceDetailCount(source);
  if (organizedCount > 0) {
    return `${organizedCount} detail${organizedCount === 1 ? '' : 's'} extracted`;
  }

  return '';
};

export const getLibrarySourcePrimaryLabel = (source = {}) => (
  getCareDocumentPurposeLabel(source)
  || getCareDocumentType(source)
  || 'Source'
);

export const filterLibrarySourcesByTab = (sources = [], tab = 'all') => {
  if (tab === 'documents') {
    return sources.filter((source) => isLibraryDocumentSource(source));
  }

  if (tab === 'images') {
    return sources.filter((source) => isLibraryImageSource(source));
  }

  return sources;
};

export const groupLibrarySourcesBySubject = (sources = []) => {
  const groupsByLabel = new Map();

  sources.forEach((source) => {
    const labels = getSubjectLabelsFromSource(source);
    labels.forEach((label) => {
      if (!groupsByLabel.has(label)) {
        groupsByLabel.set(label, {
          id: `subject:${label.toLowerCase().replace(/\s+/g, '_')}`,
          label,
          sources: [],
        });
      }
      groupsByLabel.get(label).sources.push(source);
    });
  });

  return [...groupsByLabel.values()].map((group) => ({
    ...group,
    sources: group.sources,
    itemCount: group.sources.reduce((total, source) => total + getSourceDetailCount(source), 0),
  }));
};

export const groupLibrarySourcesBySource = (sources = []) => sources.map((source) => ({
  id: source.id,
  source,
  sources: [source],
}));

export const getLibrarySearchText = (source = {}) => getCardSearchText(source);
